import { createSelector } from 'reselect';
import { flow } from 'fp-ts/function';
import {
    AvailabilityBlock,
    AvailabilityBlockState,
    PickupDistribution,
    ReleaseStrategy,
} from '@mews-commander/data-access';
import { TimeUnit } from '@mews/enums';
import {
    createAsyncMetaSelectors,
    createScopedSelector,
    DateService,
    DateTimeLocalizationInfo,
    DateTimeService,
    DateTimeUtils,
    DurationService,
    DurationUtils,
    Selector,
    TimeService,
    TimeUnitUtils,
} from '@mews/framework';
import { createHandler } from '@mews/std';
import { ApplicationState, WithScope } from '@mews-commander/types';
import { getProp } from '@mews/fp';
import { getContainer } from '@mews-commander/legacy-app-instance';
import { getEarliestDayTime } from '@mews-commander/utils';
import {
    createTimeUnitFieldInitialValue,
    formValuesSelector,
    parseFormTimeUnitDateString,
} from '@mews-commander/legacy-form';
import {
    getIsOverlappingNextTimeUnitByServiceId,
    getTimeUnitByServiceId,
} from '@mews-commander/legacy-entities/src/ducks/services';
import { getCurrentEnterpriseIanaTimeZoneIdentifier } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { getAvailabilityBlock } from '@mews-commander/legacy-entities/src/ducks/availabilityBlocks';
import { getAvailabilityBlockViewEndUtc } from '@mews-commander/legacy-entities/src/selectors/availabilityBlocks';
import { getCurrentEnterpriseAppDateTimeNow } from '@mews-commander/legacy-entities/src/selectors/session/dateTime';
import { getCurrentDateTimeLocalizationInfo } from '@mews-commander/core/legacy-globalization';
import { FormValues } from './types';
import { INITIAL_STATE } from './reducers';
import { moduleSelector } from './module';
import { getDayCountFromDuration, getShortTimeStringFromDuration } from './utils';

const scopedModuleSelector: Selector<ApplicationState, WithScope, typeof INITIAL_STATE> = createScopedSelector({
    parentSelector: moduleSelector,
    initialState: INITIAL_STATE,
});
const getModuleMeta = createSelector(scopedModuleSelector, (state) => state.meta);

export const [isAvailabilityBlockEntityFormFetching, hasAvailabilityBlockEntityFormFailed] =
    createAsyncMetaSelectors(getModuleMeta);

const timeUnitRangeConfig = createHandler(
    {
        [TimeUnit.Hour]: {
            start: 7,
            end: 0,
        },
        [TimeUnit.Day]: {
            start: 7,
            end: 7,
        },
        [TimeUnit.Month]: {
            start: 1,
            end: 0,
        },
    },
    {
        start: 7,
        end: 7,
    }
);

export const getCreateFormValues = createSelector(
    getCurrentEnterpriseAppDateTimeNow,
    getContainer,
    getTimeUnitByServiceId,
    getCurrentDateTimeLocalizationInfo,
    (now, container, timeUnit: TimeUnit, localizationInfo) => {
        const { appLocalizationComponent } = container();
        const timePart =
            timeUnit === TimeUnit.Hour
                ? TimeUnitUtils.timeUnitToDateTimePart(TimeUnit.Day)
                : TimeUnitUtils.timeUnitToDateTimePart(timeUnit);
        const timeUnitDuration = DurationUtils.timeUnitToDurationParameter(timeUnit);
        const start = DateTimeService.add(timePart)(now, timeUnitRangeConfig(timeUnit).start);
        const end = DateTimeService.add(timePart)(start, timeUnitRangeConfig(timeUnit).end);
        const timeUnitStart = DateTimeService.startOf(timeUnitDuration)(start);
        const timeUnitEnd = DateTimeService.startOf(timeUnitDuration)(end);
        const releaseAt = DateTimeService.subtractDays(DateTimeService.startOfDay(timeUnitStart), 1);

        return {
            state: AvailabilityBlockState.Confirmed,
            startValue: createTimeUnitFieldInitialValue(timeUnitStart, timeUnit, localizationInfo),
            endValue: createTimeUnitFieldInitialValue(timeUnitEnd, timeUnit, localizationInfo),
            date: appLocalizationComponent.localizeDate(releaseAt),
            time: appLocalizationComponent.localizeShortTime(releaseAt),
            pickupDistribution: PickupDistribution.AllInOneGroup,
            blockReleaseStrategy: ReleaseStrategy.FixedReleaseUtc,
            offsetDays: 7,
            offsetTime: getEarliestDayTime(localizationInfo),
        };
    }
);

export const getReleaseInitialValues = (
    { releaseStrategy }: AvailabilityBlock,
    timezone: string,
    localizationInfo: DateTimeLocalizationInfo
) => {
    const releaseAtDateTime =
        releaseStrategy.discriminator === ReleaseStrategy.FixedReleaseUtc
            ? DateTimeService.fromUtcString(releaseStrategy.value, timezone)
            : undefined;
    const rollingReleaseOffsetDuration =
        releaseStrategy.discriminator === ReleaseStrategy.RollingReleaseOffset
            ? DurationService.parse(releaseStrategy.value)
            : undefined;

    return {
        date: releaseAtDateTime && DateService.toString(DateTimeService.toDate(releaseAtDateTime), localizationInfo),
        time:
            releaseAtDateTime && TimeService.toShortString(DateTimeService.toTime(releaseAtDateTime), localizationInfo),
        blockReleaseStrategy: releaseStrategy.discriminator,
        offsetDays: rollingReleaseOffsetDuration ? getDayCountFromDuration(rollingReleaseOffsetDuration) : 7,
        offsetTime: rollingReleaseOffsetDuration
            ? getShortTimeStringFromDuration(rollingReleaseOffsetDuration, localizationInfo)
            : getEarliestDayTime(localizationInfo),
    };
};
export const getEditFormValues = createSelector(
    getAvailabilityBlock,
    getAvailabilityBlockViewEndUtc,
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    getContainer,
    getTimeUnitByServiceId,
    getCurrentDateTimeLocalizationInfo,
    (
        block,
        endUtc,
        timezone: string,
        container,
        timeUnit: TimeUnit,
        localizationInfo
    ): Partial<FormValues> | undefined => {
        if (!block || !endUtc) return undefined;

        const {
            state,
            name,
            rateId,
            reservationPurpose,
            bookerId,
            companyId,
            externalIdentifier,
            notes,
            startUtc,
            travelAgencyId,
            pickupDistribution,
            purchaseOrderNumber,
        } = block;

        const start = DateTimeService.fromUtcString(startUtc, timezone);
        const end = DateTimeService.fromUtcString(endUtc, timezone);

        const releaseInitialValues = getReleaseInitialValues(block, timezone, localizationInfo);

        return {
            state,
            name,
            rateId,
            reservationPurpose,
            bookerId,
            companyId,
            travelAgencyId,
            externalIdentifier,
            notes,
            startValue: createTimeUnitFieldInitialValue(start, timeUnit, localizationInfo),
            endValue: createTimeUnitFieldInitialValue(end, timeUnit, localizationInfo),
            pickupDistribution,
            purchaseOrderNumber,
            ...releaseInitialValues,
        };
    }
);

const availabilityBlockFormValuesSelector = formValuesSelector<FormValues>;

export const getFormStartDateString = flow(availabilityBlockFormValuesSelector, (formValues) =>
    parseFormTimeUnitDateString(formValues?.startValue)
);
export const getFormEndDateString = flow(availabilityBlockFormValuesSelector, (formValues) =>
    parseFormTimeUnitDateString(formValues?.endValue)
);

export const getMinEndDate = createSelector(
    getFormStartDateString,
    getCurrentDateTimeLocalizationInfo,
    getCurrentEnterpriseIanaTimeZoneIdentifier as Selector<ApplicationState, {}, string>,
    getProp<TimeUnit>()('timeUnit'),
    getIsOverlappingNextTimeUnitByServiceId,
    (
        startDateString,
        localizationInfo: DateTimeLocalizationInfo,
        timezone: string,
        timeUnit = TimeUnit.Day,
        isOverlappingNextTimeUnit
    ) => {
        if (startDateString && DateService.isValidString(startDateString, localizationInfo)) {
            const startDate = DateService.fromString(startDateString, localizationInfo, timezone);
            const minDate = isOverlappingNextTimeUnit
                ? TimeUnitUtils.addTimeUnitToDate(startDate, timeUnit, 1)
                : startDate;
            return DateTimeUtils.dateToStructuredDate(minDate);
        }
        return null;
    }
);

export const getMaxReleaseDate = createSelector(
    getFormEndDateString,
    getCurrentDateTimeLocalizationInfo,
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    (endDateString, localizationInfo: DateTimeLocalizationInfo, timezone: string) => {
        if (endDateString && DateService.isValidString(endDateString, localizationInfo)) {
            const startDate = DateService.fromString(endDateString, localizationInfo, timezone);
            const minEndDate = TimeUnitUtils.addTimeUnitToDate(startDate, TimeUnit.Day, -1);
            return DateTimeUtils.dateToStructuredDate(minEndDate);
        }
        return null;
    }
);
